import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  CheckCircle,
  ArrowUpRight,
  WarningCircle,
  ArrowsClockwise,
  House,
} from "@phosphor-icons/react";
import useSeo from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || "https://portal.webjesters.com";

export default function ReserveSuccess() {
  useSeo({
    title: "Reservation Confirmed — WebJesters",
    description:
      "Your WebJesters build reservation is confirmed. We'll be in touch within one business day to kick off onboarding.",
    path: "/reserve/success",
    noindex: true,
  });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({
    status: "checking", // checking | paid | received | expired | error
    payment_status: null,
    amount_total: 0,
    currency: "usd",
    metadata: {},
  });

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: "error" }));
      return;
    }

    let cancelled = false;

    const verify = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;

        if (data.status === "expired") {
          setState((s) => ({ ...s, status: "expired", payment_status: data.payment_status }));
          return;
        }

        // Reaching this page means Stripe redirected from success_url, so the
        // deposit has been processed. We surface a friendly confirmation
        // regardless of whether the proxy reports 'paid' or 'initiated' yet.
        const finalStatus = data.payment_status === "paid" ? "paid" : "received";
        setState({
          status: finalStatus,
          payment_status: data.payment_status,
          amount_total: data.amount_total,
          currency: data.currency,
          metadata: data.metadata || {},
        });
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setState((s) => ({ ...s, status: "error" }));
          return;
        }
        // Any other error — still show received (deposit is on Stripe's side)
        setState((s) => ({ ...s, status: "received" }));
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main
      id="main-content"
      data-testid="reserve-success-page"
      className="min-h-screen bg-[#0a0a0a] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-60" />

      <div className="relative max-w-xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-[13.5px] transition-colors mb-12"
        >
          <House size={14} weight="regular" />
          WebJesters
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl border border-white/[0.08] bg-[#101012] p-8 sm:p-10"
        >
          {state.status === "checking" && <CheckingState />}

          {state.status === "paid" && (
            <PaidState
              amount={state.amount_total}
              currency={state.currency}
              metadata={state.metadata}
            />
          )}

          {state.status === "received" && (
            <ReceivedState
              amount={state.amount_total}
              currency={state.currency}
              metadata={state.metadata}
            />
          )}

          {state.status === "expired" && <ExpiredState />}
          {state.status === "error" && <ErrorState />}
        </motion.div>

        <div className="mt-8 text-center text-[12px] text-zinc-400">
          Need help? Call <a href="tel:6023905623" className="text-zinc-300 hover:text-white">602-390-5623</a> or email{" "}
          <a href="mailto:team@webjesters.com" className="text-zinc-300 hover:text-white">team@webjesters.com</a>.
        </div>
      </div>
    </main>
  );
}

function CheckingState() {
  return (
    <div className="text-center py-6">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/[0.04] border border-white/10">
        <ArrowsClockwise size={22} weight="regular" className="text-white animate-spin" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight">
        Confirming your payment…
      </h1>
      <p className="mt-3 text-zinc-400 text-[14.5px] leading-relaxed">
        Verifying your payment with Stripe. This usually takes a few seconds.
      </p>
    </div>
  );
}

function PaidState({ amount, currency, metadata }) {
  const formatted = ((amount || 0) / 100).toLocaleString("en-US", {
    style: "currency",
    currency: (currency || "USD").toUpperCase(),
  });
  if (metadata?.package_id === "visibility_setup") {
    return <VisibilityState formatted={formatted} metadata={metadata} />;
  }
  if (metadata?.package_id === "gbp_setup") {
    return <GbpState formatted={formatted} metadata={metadata} />;
  }
  return (
    <div data-testid="reserve-paid">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
        <CheckCircle size={26} weight="regular" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight leading-[1.1]">
        Slot reserved.
        <br />
        Welcome to WebJesters.
      </h1>
      <p className="mt-4 text-zinc-400 text-[14.5px] leading-relaxed">
        Your <span className="text-white">{formatted}</span> deposit is in.
        We've sent your Command Center access to your email so you can submit
        your business info, services, photos, and branding details.
      </p>

      <NextStepsCards metadata={metadata} />

      <div className="mt-8 flex flex-wrap gap-3">
        <a
          href={PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reserve-cta-portal"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
        >
          Open Command Center
          <ArrowUpRight size={14} weight="bold" />
        </a>
        <Link
          to="/"
          data-testid="reserve-cta-home"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 bg-white/[0.03] text-white text-[13.5px] hover:bg-white/[0.07] transition-colors"
        >
          Back to WebJesters
        </Link>
      </div>
    </div>
  );
}

function platformsLine(md) {
  const names = { facebook: "Facebook", instagram: "Instagram" };
  const parts = ["Google Business Profile"];
  if (md?.included_platform) parts.push(names[md.included_platform] || md.included_platform);
  if (md?.second_platform) parts.push(names[md.second_platform] || md.second_platform);
  return parts.join(" + ");
}

function VisibilityState({ formatted, metadata }) {
  const monthly = metadata?.monthly_cents ? `$${Math.round(Number(metadata.monthly_cents) / 100)}/month` : "$299/month";
  return (
    <div data-testid="visibility-setup-paid">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
        <CheckCircle size={26} weight="regular" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight leading-[1.1]">
        Onboarding paid.
        <br />
        Let's get you visible.
      </h1>
      <p className="mt-4 text-zinc-400 text-[14.5px] leading-relaxed">
        Your <span className="text-white">{formatted}</span> Local Visibility
        onboarding fee is in. Greg will email you within one business day with the
        manager access we need for your Google Business Profile and chosen
        social platform{metadata?.second_platform ? "s" : ""}.
      </p>

      <div className="mt-7 grid gap-2.5 text-[13px]">
        {metadata?.included_platform && <Row k="Platforms" v={platformsLine(metadata)} />}
        <Row k="Monthly" v={`${monthly} · starts after setup is complete`} />
        <Row k="Next" v="Grant manager access to your profiles" />
        <Row k="Then" v="Setup or cleanup of your Google listing" />
        {metadata?.business && <Row k="Business" v={metadata.business} />}
      </div>

      <p className="mt-6 text-[12.5px] text-zinc-400 leading-relaxed">
        {metadata?.second_platform
          ? "Your second platform is billed only once that account's access is ready. "
          : ""}
        You can send photos, clips, and updates by text or email. For best
        quality, email originals or share a download link.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/"
          data-testid="visibility-cta-home"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
        >
          Back to WebJesters
          <ArrowUpRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function GbpState({ formatted, metadata }) {
  return (
    <div data-testid="gbp-setup-paid">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
        <CheckCircle size={26} weight="regular" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight leading-[1.1]">
        Setup paid.
        <br />
        One time, nothing recurring.
      </h1>
      <p className="mt-4 text-zinc-400 text-[14.5px] leading-relaxed">
        Your <span className="text-white">{formatted}</span> Google Business
        Profile setup is in. Greg will email you within one business day to
        walk you through granting manager access — we never ask for your
        Google password.
      </p>

      <div className="mt-7 grid gap-2.5 text-[13px]">
        <Row k="Next" v="Add WebJesters as a manager on your profile" />
        <Row k="Then" v="Setup or cleanup: details, categories, photos, review link" />
        <Row k="Finally" v="A brief completion summary" />
        {metadata?.business && <Row k="Business" v={metadata.business} />}
      </div>

      <p className="mt-6 text-[12.5px] text-zinc-400 leading-relaxed">
        Want it kept active with monthly posting later? Your $149 Local
        Visibility onboarding fee is waived — just use this same email.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/"
          data-testid="gbp-cta-home"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
        >
          Back to WebJesters
          <ArrowUpRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function ReceivedState({ amount, currency, metadata }) {
  const formatted = amount
    ? (amount / 100).toLocaleString("en-US", {
        style: "currency",
        currency: (currency || "USD").toUpperCase(),
      })
    : "$99.00";
  if (metadata?.package_id === "visibility_setup") {
    return (
      <VisibilityState
        formatted={amount ? formatted : "$149.00"}
        metadata={metadata}
      />
    );
  }
  if (metadata?.package_id === "gbp_setup") {
    return <GbpState formatted={amount ? formatted : "$299.00"} metadata={metadata} />;
  }
  return (
    <div data-testid="reserve-received">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
        <CheckCircle size={26} weight="regular" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight leading-[1.1]">
        Slot reserved.
        <br />
        Welcome to WebJesters.
      </h1>
      <p className="mt-4 text-zinc-400 text-[14.5px] leading-relaxed">
        Stripe has confirmed your <span className="text-white">{formatted}</span>{" "}
        deposit. We've sent your Command Center access to your email so you can
        submit your business info, services, photos, and branding details.
      </p>

      <NextStepsCards metadata={metadata} />

      <div className="mt-8 flex flex-wrap gap-3">
        <a
          href={PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reserve-cta-portal"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
        >
          Open Command Center
          <ArrowUpRight size={14} weight="bold" />
        </a>
        <Link
          to="/"
          data-testid="reserve-cta-home"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 bg-white/[0.03] text-white text-[13.5px] hover:bg-white/[0.07] transition-colors"
        >
          Back to WebJesters
        </Link>
      </div>
    </div>
  );
}

function NextStepsCards({ metadata }) {
  return (
    <div className="mt-7 grid gap-2.5 text-[13px]">
      <Row k="What's next" v="Complete your Command Center profile" />
      <Row k="Then" v="WebJesters reviews your info and prepares your site" />
      <Row k="Balance" v="Due before launch approval" />
      {metadata?.business && <Row k="Business" v={metadata.business} />}
    </div>
  );
}

function ExpiredState() {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">
        <WarningCircle size={22} weight="regular" />
      </div>
      <h1 className="mt-6 font-display text-3xl tracking-tight">
        That session expired.
      </h1>
      <p className="mt-3 text-zinc-400 text-[14.5px]">
        No charge was made. You can start a new reservation any time.
      </p>
      <Link
        to="/#pricing"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
      >
        Reserve again
        <ArrowUpRight size={14} weight="bold" />
      </Link>
    </div>
  );
}

function ErrorState() {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">
        <WarningCircle size={22} weight="regular" />
      </div>
      <h1 className="mt-6 font-display text-3xl tracking-tight">
        Something went wrong.
      </h1>
      <p className="mt-3 text-zinc-400 text-[14.5px]">
        We couldn't verify your session. If you were charged, contact us and
        we'll sort it out immediately.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
      >
        Back to WebJesters
        <ArrowUpRight size={14} weight="bold" />
      </Link>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
      <span className="text-zinc-500 uppercase tracking-[0.16em] text-[10.5px]">
        {k}
      </span>
      <span className="text-white text-[13px]">{v}</span>
    </div>
  );
}
